import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import { Plus, Calendar, Users, Edit, Trash2, Eye, ArrowLeft, MoreVertical, CheckCircle, XCircle, MapPin, Copy } from 'lucide-react';
import { BrutalButton } from '@/components/ui/BrutalButton';
import { BrutalBadge } from '@/components/ui/BrutalBadge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Header } from '@/components/layout/Header';
import { format } from 'date-fns';

const EventsManage = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('upcoming');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<{ id: string; title: string } | null>(null);

  useEffect(() => {
    loadEvents();
  }, [activeTab, user]);

  const loadEvents = async () => {
    if (!user) return;
    setLoading(true);

    try {
      let query = supabase
        .from('events')
        .select('*')
        .eq('organizer_id', user.id)
        .order('start_datetime', { ascending: true });

      const now = new Date().toISOString();

      if (activeTab === 'upcoming') {
        query = query.gte('start_datetime', now).eq('status', 'published');
      } else if (activeTab === 'past') {
        query = query.lt('start_datetime', now).neq('status', 'draft');
      } else if (activeTab === 'drafts') {
        query = query.eq('status', 'draft');
      }

      const { data, error } = await query;
      if (error) throw error;
      setEvents(data || []);
    } catch (error: any) {
      console.error('Error loading events:', error);
      toast({
        title: t('common.error'),
        description: error.message,
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (event: { id: string; title: string }) => {
    setEventToDelete(event);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!eventToDelete) return;

    try {
      // 1. Get the event to retrieve cover_image_url before deletion
      const { data: event } = await supabase
        .from('events')
        .select('cover_image_url')
        .eq('id', eventToDelete.id)
        .single();

      // 2. If there's a cover image, delete it from storage
      if (event?.cover_image_url) {
        try {
          const url = new URL(event.cover_image_url);
          const pathMatch = url.pathname.match(/\/storage\/v1\/object\/public\/cover-images\/(.+)/);
          if (pathMatch) {
            const filePath = decodeURIComponent(pathMatch[1]);
            await supabase.storage.from('cover-images').remove([filePath]);
          }
        } catch (storageError) {
          console.warn('Could not delete cover image:', storageError);
          // Continue with event deletion even if image deletion fails
        }
      }

      // 3. Delete the event (reports will be deleted automatically via CASCADE)
      const { error } = await supabase.from('events').delete().eq('id', eventToDelete.id);
      if (error) throw error;

      toast({
        title: t('events.deleted'),
        description: t('events.deletedDesc'),
      });

      loadEvents();
    } catch (error: any) {
      console.error('Error deleting event:', error);
      toast({
        title: t('common.error'),
        description: error.message,
        variant: 'error',
      });
    } finally {
      setDeleteDialogOpen(false);
      setEventToDelete(null);
    }
  };

  const handleDuplicateEvent = async (event: any) => {
    try {
      const { error } = await supabase.from('events').insert({
        organizer_id: user!.id,
        title: event.title + ' (Copia)',
        description_it: event.description_it,
        description_en: event.description_en,
        description_es: event.description_es,
        event_type: event.event_type,
        event_format: event.event_format,
        location_name: event.location_name,
        location_address: event.location_address,
        location_city: event.location_city,
        location_url: event.location_url,
        location_latitude: event.location_latitude,
        location_longitude: event.location_longitude,
        start_datetime: event.start_datetime,
        end_datetime: event.end_datetime,
        timezone: event.timezone,
        max_participants: event.max_participants,
        requires_approval: event.requires_approval,
        external_ticket_url: event.external_ticket_url,
        external_platform: event.external_platform,
        price: event.price,
        currency: event.currency,
        cover_image_url: event.cover_image_url,
        status: 'draft',
        slug: event.title.toLowerCase().replace(/[^a-z0-9]+/g, '-') + '-' + Math.random().toString(36).substring(2, 8),
      });

      if (error) throw error;

      toast({
        title: t("common.duplicateSuccess"),
        description: t("common.duplicateSuccessDesc"),
      });

      setActiveTab('drafts');
      loadEvents();
    } catch (error: any) {
      toast({
        title: t("common.duplicateError"),
        description: error.message,
        variant: 'error' as any,
      });
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'published':
        return <BrutalBadge variant="success"><CheckCircle className="w-3 h-3 mr-1" /> {t("events.status.published")}</BrutalBadge>;
      case 'draft':
        return <BrutalBadge variant="secondary">{t("events.status.draft")}</BrutalBadge>;
      case 'cancelled':
        return <BrutalBadge variant="error"><XCircle className="w-3 h-3 mr-1" /> {t("events.status.cancelled")}</BrutalBadge>;
      case 'completed':
        return <BrutalBadge variant="secondary">{t("events.status.completed")}</BrutalBadge>;
      default:
        return <BrutalBadge variant="secondary">{status}</BrutalBadge>;
    }
  };

  const getEmptyStateContent = () => {
    let title = '';
    let description = '';
    
    if (activeTab === 'upcoming') {
      title = t('events.noUpcoming');
      description = t('events.noUpcomingDesc');
    } else if (activeTab === 'past') {
      title = t('events.noPast');
      description = t('events.noPastDesc');
    } else {
      title = t('events.noDrafts');
      description = t('events.noDraftsDesc');
    }
    
    return { title, description };
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-8">
        <Breadcrumb className="mb-4">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/dashboard">Dashboard</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{t('events.myEvents')}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="flex items-center gap-4 mb-2">
          <button
            onClick={() => navigate("/dashboard")}
            className="p-2 hover:bg-accent transition-colors rounded-lg"
            aria-label={t("common.back")}
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h1 className="text-3xl font-heading font-bold text-dark">{t('events.myEvents')}</h1>
        </div>
        <p className="text-muted-foreground mb-8 ml-14">{t('events.myEventsDesc')}</p>

        <div className="flex justify-end mb-6">
          <BrutalButton variant="primary" onClick={() => navigate('/dashboard/events/create')}>
            <Plus className="w-4 h-4 mr-2" />
            <span className="md:hidden">{t('common.create')}</span>
            <span className="hidden md:inline">{t('events.createEvent')}</span>
          </BrutalButton>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full sm:w-auto p-0 h-auto mb-6 bg-transparent border-0">
            <TabsTrigger 
              value="upcoming"
              className="border-r-[3px] border-dark data-[state=active]:bg-primary data-[state=active]:text-dark font-heading font-bold uppercase px-6 py-3 rounded-none first:rounded-l-none last:rounded-r-none"
            >
              {t('events.upcoming')}
            </TabsTrigger>
            <TabsTrigger 
              value="past"
              className="border-r-[3px] border-dark data-[state=active]:bg-primary data-[state=active]:text-dark font-heading font-bold uppercase px-6 py-3 rounded-none"
            >
              {t('events.past')}
            </TabsTrigger>
            <TabsTrigger 
              value="drafts"
              className="data-[state=active]:bg-primary data-[state=active]:text-dark font-heading font-bold uppercase px-6 py-3 rounded-none first:rounded-l-none last:rounded-r-none"
            >
              {t('events.drafts')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-0">
            {loading ? (
              <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="h-32 bg-card border-[3px] border-dark animate-pulse" />
                ))}
              </div>
            ) : events.length === 0 ? (
              <div className="text-center py-16">
                <Calendar className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-heading text-xl font-bold mb-2">{getEmptyStateContent().title}</h3>
                <p className="text-muted-foreground mb-6">{getEmptyStateContent().description}</p>
                <BrutalButton onClick={() => navigate('/dashboard/events/create')}>
                  <Plus className="w-4 h-4 mr-2" /> {t('events.createEvent')}
                </BrutalButton>
              </div>
            ) : (
              <div className="space-y-4">
                {events.map((event) => (
                  <div 
                    key={event.id}
                    className="bg-card border-[3px] border-dark shadow-[4px_4px_0px_#101322] p-6"
                  >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h2 className="font-heading text-xl font-bold text-dark">
                            {event.title}
                          </h2>
                          {getStatusBadge(event.status)}
                        </div>
                        <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {format(new Date(event.start_datetime), 'PPp')}
                          </span>
                          {event.location_city && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-4 h-4" />
                              {event.location_city}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Container stats + azioni - su mobile in colonna, su desktop inline */}
                      <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-4">
                        {/* Riga 1: Stats partecipanti */}
                        {event.event_type === 'free' && (
                          <div className="flex items-center gap-4 justify-start md:justify-end">
                            <div className="text-center min-w-[48px]">
                              <div className="flex items-center justify-center gap-1 text-muted-foreground">
                                <Users className="w-4 h-4" />
                                <span className="font-bold text-dark">{event.current_participants || 0}</span>
                                {event.max_participants && (
                                  <span className="text-muted-foreground">/ {event.max_participants}</span>
                                )}
                              </div>
                              <span className="text-xs text-muted-foreground">{t("events.participants")}</span>
                            </div>
                          </div>
                        )}

                        {/* Riga 2: Pulsanti azione */}
                        <div className="flex items-center gap-2 justify-end">
                          <Link to={`/event/${event.slug}`} target="_blank">
                            <BrutalButton variant="outline" size="sm">
                              <Eye className="w-4 h-4" />
                            </BrutalButton>
                          </Link>
                          
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <BrutalButton variant="outline" size="sm">
                                <MoreVertical className="w-4 h-4" />
                              </BrutalButton>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="border-[3px] border-dark">
                              <DropdownMenuItem onClick={() => navigate(`/dashboard/events/${event.id}/edit`)}>
                                <Edit className="w-4 h-4 mr-2" /> {t("common.edit")}
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDuplicateEvent(event)}>
                                <Copy className="w-4 h-4 mr-2" /> {t("common.duplicateAsDraft")}
                              </DropdownMenuItem>
                              {event.event_type === 'free' && (
                                <DropdownMenuItem onClick={() => navigate(`/dashboard/events/${event.id}/participants`)}>
                                  <Users className="w-4 h-4 mr-2" /> {t("events.participants")}
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                onClick={() => handleDeleteClick({ id: event.id, title: event.title })} 
                                className="text-destructive"
                              >
                                <Trash2 className="w-4 h-4 mr-2" /> {t("common.delete")}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Delete Event Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent className="border-[3px] border-dark shadow-[6px_6px_0px_#101322] w-[90vw] sm:w-auto sm:max-w-md">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-heading text-xl">
                {t("events.confirmDelete")}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t("common.cannotBeUndone")}
              </AlertDialogDescription>
            </AlertDialogHeader>
            {eventToDelete && (
              <div className="p-3 bg-destructive/10 border-[2px] border-dark">
                <p className="font-heading font-bold text-dark">"{eventToDelete.title}"</p>
              </div>
            )}
            <AlertDialogFooter className="gap-3">
              <BrutalButton
                variant="secondary"
                size="sm"
                onClick={() => setDeleteDialogOpen(false)}
              >
                {t("common.cancel")}
              </BrutalButton>
              <BrutalButton
                variant="danger"
                size="sm"
                onClick={handleDeleteConfirm}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {t("common.delete")}
              </BrutalButton>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </main>
    </div>
  );
};

export default EventsManage;
